import React, { useContext, useEffect, useState, useCallback } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Typography,
	Box,
	CircularProgress,
	makeStyles,
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import isNativeApp from "../../utils/isNativeApp";
import { i18n } from "../../translate/i18n";

const GODEAL_COMPANY_ID = "1";

const useStyles = makeStyles((theme) => ({
	banner: {
		position: "sticky",
		top: 0,
		zIndex: 1100,
		background:
			"linear-gradient(90deg, #128c7e 0%, #25d366 100%)",
		color: "#fff",
		padding: "10px 20px",
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		fontSize: 14,
		fontWeight: 500,
		flexWrap: "wrap",
		gap: 8,
	},
	bannerCta: {
		background: "rgba(255,255,255,0.18)",
		color: "#fff",
		padding: "6px 14px",
		borderRadius: 6,
		fontWeight: 600,
		"&:hover": { background: "rgba(255,255,255,0.28)" },
	},
	planList: {
		display: "grid",
		gridTemplateColumns: "1fr",
		gap: 12,
		marginTop: 12,
		[theme.breakpoints.up("sm")]: {
			gridTemplateColumns: "1fr 1fr 1fr",
		},
	},
	planCard: {
		border: "1px solid #e2e8f0",
		// Explicit light surface so the near-black plan name/price stay legible
		// even when the Dialog Paper is dark (dark-mode); hover/selected below
		// override this. Restores readability without any layout/typography change.
		background: "#fff",
		borderRadius: 12,
		padding: 16,
		cursor: "pointer",
		transition: "all .15s ease",
		"&:hover": {
			borderColor: "#25d366",
			background: "#f0fdf4",
		},
	},
	planCardSelected: {
		borderColor: "#128c7e",
		background: "#dcfce7",
	},
	planName: { fontSize: 14, fontWeight: 700, color: "#0b141a" },
	planPrice: { fontSize: 22, fontWeight: 800, color: "#0b141a" },
	planDesc: { fontSize: 12, color: "#54656f" },
	enterpriseCard: {
		border: "1.5px dashed #128c7e",
		borderRadius: 12,
		padding: 16,
		// Same faint green wash as before, now over an explicit white base so
		// the card + its near-black text stay readable on a dark Dialog Paper.
		background:
			"linear-gradient(160deg, rgba(18,140,126,0.05) 0%, rgba(37,211,102,0.02) 100%), #fff",
		display: "flex",
		flexDirection: "column",
		gap: 8,
	},
	enterpriseBadge: {
		alignSelf: "flex-start",
		background: "#128c7e",
		color: "#fff",
		fontSize: 9.5,
		fontWeight: 800,
		letterSpacing: "0.18em",
		textTransform: "uppercase",
		borderRadius: 999,
		padding: "3px 9px",
		lineHeight: 1.2,
	},
	enterpriseCta: {
		marginTop: 8,
		background: "#128c7e",
		color: "#fff",
		padding: "8px 14px",
		borderRadius: 8,
		fontWeight: 700,
		fontSize: 13,
		textAlign: "center",
		textDecoration: "none",
		display: "inline-block",
		"&:hover": { background: "#0e7a6e", color: "#fff" },
	},
	dangerLink: {
		color: "#b91c1c",
		fontSize: 12,
		marginTop: 16,
		cursor: "pointer",
		textDecoration: "underline",
	},
}));

const TrialGate = () => {
	const classes = useStyles();
	const history = useHistory();
	const { user } = useContext(AuthContext);
	const companyId = String(user?.companyId || "");
	// Buying a plan is the company administrator's act — the backend enforces it,
	// and this keeps the UI honest instead of offering a button that 403s.
	const canChoosePlan = user?.profile === "admin";
	const [trial, setTrial] = useState(null);
	const [plans, setPlans] = useState([]);
	const [selectedPlanId, setSelectedPlanId] = useState(null);
	const [showPicker, setShowPicker] = useState(false);
	const [showDelete, setShowDelete] = useState(false);
	const [deletePassword, setDeletePassword] = useState("");
	const [deleteConfirm, setDeleteConfirm] = useState("");
	const [busy, setBusy] = useState(false);

	const isProtectedTenant = !companyId || companyId === GODEAL_COMPANY_ID;

	const fetchSubscription = useCallback(async () => {
		if (isProtectedTenant) return;
		try {
			const { data } = await api.get("/billing/subscription");
			setTrial(data.trial);
		} catch (_) {
			// silent — subscription endpoint may transiently fail; we don't
			// want to block the entire UI because of a polling glitch.
		}
	}, [isProtectedTenant]);

	const fetchPlans = useCallback(async () => {
		try {
			const { data } = await api.get("/billing/plans");
			setPlans(Array.isArray(data) ? data : data?.plans || []);
		} catch (_) {}
	}, []);

	useEffect(() => {
		if (isProtectedTenant) return;
		fetchSubscription();
		const id = setInterval(fetchSubscription, 5 * 60 * 1000);
		return () => clearInterval(id);
	}, [fetchSubscription, isProtectedTenant]);

	useEffect(() => {
		if (showPicker && plans.length === 0) fetchPlans();
	}, [showPicker, plans.length, fetchPlans]);

	// Apple App Store Guideline 3.1.1: never show the trial banner / blocking
	// price modal inside the native iOS app — Apple rejects any external
	// payment prompt. Native users get the app un-gated; subscription is
	// managed by the company owner on whapiz.com.
	if (isNativeApp()) return null;
	if (isProtectedTenant || !trial?.isTrial) return null;

	const onChoosePlan = async () => {
		// Fail-closed (Apple 3.1.1): never reach an external (Tranzila) checkout
		// from inside the native app, even if the render-time guard above slipped.
		if (isNativeApp()) return;
		if (!selectedPlanId) {
			toast.warn("Please select a plan");
			return;
		}
		const plan = plans.find((p) => p.id === selectedPlanId);
		if (!plan) {
			toast.warn("Please select a plan");
			return;
		}
		try {
			setBusy(true);
			// Use the Tranzila hosted-checkout flow instead of the
			// /billing/admin/subscriptions endpoint, which requires
			// super-admin scope and 403'd for regular tenants. The
			// callback handler activates the subscription once Tranzila
			// confirms payment.
			const { data } = await api.post("/payment-links", {
				amount: plan.value,
				currency: plan.currency || "ILS",
				language: "heb",
				description: `Whapiz ${plan.name} — ${plan.billingInterval || "monthly"}`,
				planId: plan.id,
				type: "subscription",
			});
			if (data?.url) {
				window.location.href = data.url;
				return;
			}
			toastError(new Error("Could not generate checkout link"));
		} catch (err) {
			toastError(err);
		} finally {
			setBusy(false);
		}
	};

	const onConfirmDelete = async () => {
		if (deleteConfirm !== "DELETE") {
			toast.warn('Type "DELETE" to confirm');
			return;
		}
		try {
			setBusy(true);
			await api.delete("/account", {
				data: { password: deletePassword, confirmText: deleteConfirm },
			});
			localStorage.removeItem("token");
			localStorage.removeItem("companyId");
			localStorage.removeItem("userId");
			toast.success("Your account has been deleted.");
			history.push("/login");
		} catch (err) {
			toastError(err);
		} finally {
			setBusy(false);
		}
	};

	const daysLeft =
		typeof trial.daysRemaining === "number" ? trial.daysRemaining : null;

	return (
		<>
			{!trial.isTrialExpired && (
				<div className={classes.banner}>
					<span>
						{daysLeft !== null
							? `${daysLeft} day${daysLeft === 1 ? "" : "s"} left in your free trial`
							: "You're on a free trial"}
					</span>
					{canChoosePlan && (
						<button
							className={classes.bannerCta}
							onClick={() => setShowPicker(true)}
						>
							Choose a plan
						</button>
					)}
				</div>
			)}

			<Dialog
				open={trial.isTrialExpired || showPicker}
				disableBackdropClick={trial.isTrialExpired}
				disableEscapeKeyDown={trial.isTrialExpired}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>
					{trial.isTrialExpired
						? "Your free trial has ended"
						: "Pick a plan to continue"}
				</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="textSecondary">
						{!canChoosePlan
							? trial.isTrialExpired
								? "Your administrator can choose a plan to restore access."
								: "Your administrator chooses the plan for this workspace."
							: trial.isTrialExpired
								? "Choose a plan to keep using Whapiz, or delete your account."
								: "Pick the plan that fits your team — switch or cancel any time."}
					</Typography>

					<Box className={classes.planList} hidden={!canChoosePlan}>
						{plans.map((plan) => (
							<div
								key={plan.id}
								className={`${classes.planCard} ${
									selectedPlanId === plan.id
										? classes.planCardSelected
										: ""
								}`}
								onClick={() => setSelectedPlanId(plan.id)}
							>
								<div className={classes.planName}>{plan.name}</div>
								<div className={classes.planPrice}>
									{plan.currency || "ILS"} {plan.value}
								</div>
								<div className={classes.planDesc}>
									{plan.description || ""}
								</div>
							</div>
						))}
						{plans.length === 0 && (
							<Box display="flex" justifyContent="center" p={2}>
								<CircularProgress size={20} />
							</Box>
						)}
						{/* Always-shown Enterprise card — not in DB, contact-sales
						    CTA instead of self-serve checkout. Sits after the
						    dynamic plans so high-volume teams have a path even
						    if no admin tier matches. */}
						<div className={classes.enterpriseCard}>
							<span className={classes.enterpriseBadge}>
								{i18n.t("enterprisePlan.title", { defaultValue: "Enterprise" })}
							</span>
							<div className={classes.planName}>
								{i18n.t("enterprisePlan.title", { defaultValue: "Enterprise" })}
							</div>
							<div className={classes.planPrice} style={{ fontSize: 18 }}>
								{i18n.t("enterprisePlan.pricing", { defaultValue: "Custom pricing" })}
							</div>
							<div className={classes.planDesc}>
								{i18n.t("enterprisePlan.description", {
									defaultValue: "Tailored plan for high-volume teams",
								})}
							</div>
							<a
								className={classes.enterpriseCta}
								href="mailto:sales@whapiz.com?subject=Enterprise%20plan%20enquiry"
							>
								{i18n.t("enterprisePlan.ctaContactSales", {
									defaultValue: "Contact Sales",
								})}
							</a>
						</div>
					</Box>

					{trial.isTrialExpired && (
						<div
							className={classes.dangerLink}
							onClick={() => setShowDelete(true)}
						>
							I don't want to continue — delete my account
						</div>
					)}
				</DialogContent>
				<DialogActions>
					{!trial.isTrialExpired && (
						<Button onClick={() => setShowPicker(false)} disabled={busy}>
							Later
						</Button>
					)}
					{canChoosePlan && (
						<Button
							color="primary"
							variant="contained"
							disabled={busy || !selectedPlanId}
							onClick={onChoosePlan}
						>
							{busy ? "Saving…" : "Continue with this plan"}
						</Button>
					)}
				</DialogActions>
			</Dialog>

			<Dialog open={showDelete} maxWidth="xs" fullWidth>
				<DialogTitle>Delete account permanently</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="textSecondary">
						This will permanently remove your company and all data
						(contacts, tickets, conversations, settings). This cannot
						be undone.
					</Typography>
					<TextField
						label="Your password"
						type="password"
						fullWidth
						margin="normal"
						value={deletePassword}
						onChange={(e) => setDeletePassword(e.target.value)}
					/>
					<TextField
						label='Type "DELETE" to confirm'
						fullWidth
						margin="normal"
						value={deleteConfirm}
						onChange={(e) => setDeleteConfirm(e.target.value)}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setShowDelete(false)} disabled={busy}>
						Cancel
					</Button>
					<Button
						style={{ color: "#b91c1c" }}
						disabled={busy || deleteConfirm !== "DELETE"}
						onClick={onConfirmDelete}
					>
						{busy ? "Deleting…" : "Delete account"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default TrialGate;
